import { Breadcrumb, Col, Row, DatePicker, Select, message } from "antd";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useMutation } from "react-query";
import { useEffect, useState } from "react";

import Title from "../UI/Title/Title";
import { BalanceList } from "./balanceList";
import ResetButton from "../UI/ResetButton/resetbutton";
import { getSupplierBalance, getSupplierDropdown } from "../../api/supplier";
import { Pagination } from "./types";

const SupplierBalance = () => {
  const { t } = useTranslation("common");
  const { RangePicker } = DatePicker;
  const { state } = useLocation();

  const [tableKey, setTableKey] = useState<number>(0);
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [suppliers, setSuppliers] = useState<{ value: number; label: string }[]>([]);
  const [supplier, setSupplier] = useState<any>({ value: state?.id, label: state?.name });
  const [supplierBalance, setSupplierBalance] = useState<any[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    current: 1,
    pageSize: 10,
    total: 0,
    limit: 10,
  });

  const { mutateAsync: getSuppliersMutation } = useMutation(
    "suppliers",
    getSupplierDropdown,
    {
      onSuccess: ({ data }) => {
        setSuppliers(data.map((sup: any) => {
          return { value: sup.id, label: sup.name };
        })
        );
        setSupplier({
          label: data[0].name,
          value: data[0].id,
        });
        getSupplierBalanceMutation({
          supplierId: data[0].id,
          limit: pagination.limit,
          offset: (pagination.current - 1) * pagination.pageSize,
          from,
          to
        })
      },
    }
  );

  const { mutateAsync: getSupplierBalanceMutation, isLoading: supplierBalanceLoading, } = useMutation(
    "supplierBalance",
    ({ supplierId, limit, offset, from, to }: { supplierId: number, limit?: number, offset?: number, from?: string, to?: string }) => getSupplierBalance({
      supplierId, limit, offset, from, to
    }),
    {
      onSuccess: ({ data }: any) => {
        setSupplierBalance(data?.data);
        if (data.total !== pagination.total) {
          setPagination((prevPagination: any) => ({
            ...prevPagination,
            total: data.total,
            current: 1
          }));
        }
      },
      onError: ({ data }) => {
        message.error(data.data ? data.data.message : "Error loading supplier's transactions")
      }
    }
  );

  const resetFilters = async () => {
    if (!from && !to) return
    setTableKey((tableKey) => tableKey + 1);
    setFrom("");
    setTo("");
    const { data } = await getSupplierBalanceMutation({
      supplierId: supplier.value,
      limit: pagination.limit
    })
    setSupplierBalance(data.data)
    setPagination(prevPagination => ({
      ...prevPagination,
      current: 1
    }))
  };

  const handleTableChange = async (pagination: any) => {
    setPagination(pagination);
    const { data } = await getSupplierBalanceMutation({
      supplierId: supplier.value,
      limit: pagination.limit,
      offset: (pagination.current - 1) * pagination.pageSize,
      from: from,
      to: to
    })
    setSupplierBalance(data.data)
  };

  const handleDateChange = async (dateString: any[]) => {
    setFrom(dateString[0]);
    setTo(dateString[1]);
    const { data } = await getSupplierBalanceMutation({
      supplierId: supplier.value,
      limit: pagination.limit,
      offset: (pagination.current - 1) * pagination.pageSize,
      from: dateString[0],
      to: dateString[1]
    })
    setSupplierBalance(data.data)
    setPagination((prevPagination: any) => ({
      ...prevPagination,
      total: data.total,
      current: 1
    }));
  }

  const handleSupplierSelectorChange = async (record: any) => {
    setSupplier(record);
    const { data } = await getSupplierBalanceMutation({
      supplierId: record.value,
      limit: pagination.limit,
      offset: (pagination.current - 1) * pagination.pageSize,
      from,
      to
    })
    setSupplierBalance(data.data)
  }

  useEffect(() => {
    getSuppliersMutation()
  }, []);

  return (
    <>
      <Row gutter={[10, 10]}>
        <Col offset={1} span={20}>
          <Breadcrumb
            items={[
              {
                title: <Link to="/main">{t("home")}</Link>,
              },
              {
                title: <Link to="/suppliers">{t("suppliers-list")}</Link>,
              },
              {
                title: (
                  <span className="breadcrumbcolor">
                    {t("supplier-transactions")}
                  </span>
                ),
              },
            ]}
          />
        </Col>
        <Col offset={1} span={22}>
          {supplier.label && <Title title={`${supplier?.label} ${t("transactions")}`} />}
        </Col>
        <Col offset={1} span={5}>
          <RangePicker
            style={{ width: "100%" }}
            size="large"
            onChange={(date, dateString) => {
              handleDateChange(dateString)
            }}
            format={"MM/DD/YYYY"}
            key={tableKey}
          />
        </Col>
        <Col span={4}>
          {supplier?.value && (
            <Select
              defaultValue={supplier?.value}
              style={{ width: "100%", height: "40px" }}
              onChange={(v, record) => {
                handleSupplierSelectorChange(record)
              }}
              options={suppliers}
              size="large"
              placeholder={`${t("select-supplier")}`}
            />
          )}
        </Col>
        <Col
          offset={11}
          span={2}
          style={{ display: "flex", justifyContent: "flex-end", gap: "20px" }}
        >
          <ResetButton
            onClick={resetFilters}
          />
        </Col>
        <Col offset={1} span={22}>
          <BalanceList
            supplierBalance={supplierBalance}
            setSupplierBalance={setSupplierBalance}
            supplierBalanceLoading={supplierBalanceLoading}
            getSupplierBalanceMutation={getSupplierBalanceMutation}
            pagination={pagination}
            setPagination={setPagination}
            from={from}
            to={to}
            handleTableChange={handleTableChange}
          />
        </Col>
      </Row>
    </>
  );
}

export default SupplierBalance;
